import type { Prayer } from '@/types/prayer';
import { prayerContentSnapshot } from '@/lib/outbound-prayer-authorization';

/**
 * A private-share confirmation is valid only for the exact prayer revision the
 * user reviewed. Authored dates are not content-version tokens: automatic
 * metadata may change without changing them. The complete transient snapshot
 * makes those changes require fresh consent too.
 */
export interface SharePrivacyGrant {
  prayerId: string;
  updatedAt: string;
  isPrivate: boolean;
  snapshot: string;
}

export function sharePrivacyGrantFor(prayer: Prayer): SharePrivacyGrant {
  return {
    prayerId: prayer.id,
    updatedAt: prayer.updatedAt,
    isPrivate: prayer.isPrivate === true,
    snapshot: prayerContentSnapshot(prayer),
  };
}

export function sharePrivacyGrantsEqual(
  left: SharePrivacyGrant | null | undefined,
  right: SharePrivacyGrant | null | undefined,
): boolean {
  return Boolean(
    left &&
      right &&
      left.prayerId === right.prayerId &&
      left.updatedAt === right.updatedAt &&
      left.isPrivate === right.isPrivate &&
      typeof left.snapshot === 'string' && left.snapshot !== '' &&
      left.snapshot === right.snapshot,
  );
}

/**
 * Public prayers need no grant. A private prayer is authorized only when the
 * caller presents the confirmation captured for its exact current revision.
 */
export function isShareAuthorized(
  prayer: Prayer | null | undefined,
  grant: SharePrivacyGrant | null | undefined,
): boolean {
  if (!prayer) return false;
  if (prayer.isPrivate !== true) return true;
  try { return sharePrivacyGrantsEqual(grant, sharePrivacyGrantFor(prayer)); }
  catch { return false; }
}
